import React, { useState } from 'react';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { APP_VERSION } from '../version';

const claimFreeTrialFn = httpsCallable(functions, 'claimFreeTrial');

const LicenseGate = ({ user, onLicenseActivated }) => {
    const [licenseKeyInput, setLicenseKeyInput] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [claimLoading, setClaimLoading] = useState(false);
    const [claimSuccess, setClaimSuccess] = useState(false);

    const handleActivate = async (e) => {
        e.preventDefault();
        const key = licenseKeyInput.trim().toUpperCase();

        if (!key) {
            setError('Sila masukkan License Key.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // ★ SECURITY: Call Cloud Function instead of writing directly to Firestore
            const activateLicenseFn = httpsCallable(functions, 'activateLicense');
            const result = await activateLicenseFn({ licenseKey: key });

            if (result.data.success) {
                setSuccess(true);
                setTimeout(() => {
                    onLicenseActivated();
                }, 1500);
            }

        } catch (err) {
            console.error('[License] Activation failed:', err);

            // Parse Cloud Function error messages
            const errorCode = err.code || '';
            const errorMsg = err.message || '';

            if (errorCode === 'functions/not-found' || errorMsg.includes('tidak sah')) {
                setError('License Key tidak sah. Sila semak semula.');
            } else if (errorCode === 'functions/already-exists' || errorMsg.includes('sudah digunakan')) {
                setError('License Key ini sudah digunakan oleh pengguna lain.');
            } else if (errorCode === 'functions/resource-exhausted' || errorMsg.includes('banyak percubaan')) {
                setError('Terlalu banyak percubaan. Sila tunggu 1 jam sebelum cuba lagi.');
            } else if (errorCode === 'functions/invalid-argument') {
                setError('Format License Key tidak sah.');
            } else if (errorCode === 'functions/internal') {
                setError('Masalah server. Sila cuba lagi sebentar.');
            } else if (errorCode === 'functions/unavailable' || errorCode === 'functions/deadline-exceeded') {
                setError('Server sibuk. Sila cuba lagi dalam beberapa saat.');
            } else {
                // Show actual error for debugging
                const detail = errorMsg || errorCode || 'Unknown error';
                setError(`Gagal mengaktifkan lesen: ${detail}`);
                console.error('[License] Full error object:', JSON.stringify(err, null, 2));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClaimTrial = async () => {
        setClaimLoading(true);
        setError('');
        try {
            await claimFreeTrialFn({});
            setClaimSuccess(true);
            setTimeout(() => onLicenseActivated(), 1500);
        } catch (err) {
            if (err.code === 'functions/already-exists') {
                setError('Percubaan percuma sudah dituntut. Jika anda pelanggan berbayar, sila perbaharui di app.sesaat.ai/pricing atau hubungi admin.');
            } else {
                setError('Gagal menuntut percubaan percuma. Sila cuba lagi.');
            }
            setClaimLoading(false);
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
    };

    return (
        <div className="license-gate-page">
            {/* Animated background */}
            <div className="license-gate-bg">
                <div className="license-gate-particle license-gate-p1"></div>
                <div className="license-gate-particle license-gate-p2"></div>
                <div className="license-gate-particle license-gate-p3"></div>
                <div className="license-gate-particle license-gate-p4"></div>
            </div>

            <div className="license-gate-container">
                {/* Logo */}
                <div className="license-gate-branding">
                    <div className="license-gate-logo">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2Z" fill="white" stroke="white" strokeWidth="0.5" />
                        </svg>
                    </div>
                    <div className="license-gate-brand-text">
                        <span className="license-gate-brand-name">SESAAT</span>
                        <span className="license-gate-version">v{APP_VERSION}</span>
                    </div>
                </div>

                {/* Shield Icon */}
                <div className="license-gate-shield">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        <path d="M9 12l2 2 4-4" strokeWidth="2" />
                    </svg>
                </div>

                {/* Card */}
                <div className="license-gate-card">
                    {success ? (
                        <div className="license-gate-success">
                            <div className="license-gate-success-icon">
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" fill="#22c55e" opacity="0.15" />
                                    <path d="M9 12l2 2 4-4" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="license-gate-success-title">Lesen Diaktifkan!</h3>
                            <p className="license-gate-success-desc">Selamat datang ke SESAAT. Sila tunggu...</p>
                        </div>
                    ) : (
                        <>
                            <h2 className="license-gate-title">Akaun Belum Aktif</h2>
                            <p className="license-gate-desc">
                                Akaun <strong>{user?.email}</strong> belum aktif. Mulakan percubaan percuma di bawah, atau langgan untuk akses penuh.
                            </p>

                            {error && (
                                <div className="license-gate-error">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="#f87171" strokeWidth="2" />
                                        <path d="M12 8v4M12 16h.01" stroke="#f87171" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    {error}
                                </div>
                            )}

                            <div style={{ margin: '16px 0 0', textAlign: 'center' }}>
                                {claimSuccess ? (
                                    <p style={{ color: '#34d399', fontSize: 13, fontWeight: 600 }}>✅ 5 coins dituntut! Memuatkan...</p>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleClaimTrial}
                                        disabled={claimLoading}
                                        style={{
                                            width: '100%', padding: '10px 16px', borderRadius: 10,
                                            background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                                            color: '#34d399', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                            transition: 'all 0.2s', opacity: claimLoading ? 0.6 : 1
                                        }}
                                    >
                                        {claimLoading ? (
                                            <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #34d399', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                        ) : (
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                                            </svg>
                                        )}
                                        Nak cuba dulu — Tuntut 5 Coins Percuma
                                    </button>
                                )}
                            </div>

                            <a href="https://app.sesaat.ai/pricing" target="_blank" rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', margin: '12px 0 0', fontSize: 12, color: '#93c5fd', textDecoration: 'none' }}>
                                Sudah langgan tapi tamat tempoh? Perbaharui di sini →
                            </a>

                            <button
                                type="button"
                                onClick={handleLogout}
                                className="license-gate-logout"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
                                </svg>
                                Log Keluar
                            </button>
                        </>
                    )}
                </div>
            </div>

            <style>{`
                .license-gate-page {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #0a0e1a 0%, #0d1321 25%, #111827 50%, #0f172a 75%, #0a0e1a 100%);
                    position: relative;
                    overflow: hidden;
                    padding: 16px;
                }

                .license-gate-bg {
                    position: absolute;
                    inset: 0;
                    pointer-events: none;
                    overflow: hidden;
                }

                .license-gate-particle {
                    position: absolute;
                    border-radius: 50%;
                    opacity: 0.06;
                    animation: lgFloat 20s ease-in-out infinite;
                }

                .license-gate-p1 {
                    width: 350px; height: 350px;
                    background: radial-gradient(circle, #f59e0b, transparent 70%);
                    top: -100px; left: -80px;
                    animation-duration: 22s;
                }
                .license-gate-p2 {
                    width: 300px; height: 300px;
                    background: radial-gradient(circle, #ef4444, transparent 70%);
                    bottom: -80px; right: -60px;
                    animation-duration: 18s; animation-delay: -4s;
                }
                .license-gate-p3 {
                    width: 200px; height: 200px;
                    background: radial-gradient(circle, #3b82f6, transparent 70%);
                    top: 50%; left: 50%;
                    animation-duration: 25s; animation-delay: -8s;
                }
                .license-gate-p4 {
                    width: 250px; height: 250px;
                    background: radial-gradient(circle, #8b5cf6, transparent 70%);
                    top: 20%; right: 15%;
                    animation-duration: 20s; animation-delay: -12s;
                }

                @keyframes lgFloat {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    25% { transform: translate(20px, -30px) scale(1.08); }
                    50% { transform: translate(-15px, 15px) scale(0.95); }
                    75% { transform: translate(25px, 20px) scale(1.03); }
                }

                .license-gate-container {
                    position: relative;
                    z-index: 10;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    width: 100%;
                    max-width: 420px;
                    animation: lgFadeUp 0.6s ease-out;
                }

                @keyframes lgFadeUp {
                    from { opacity: 0; transform: translateY(24px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .license-gate-branding {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .license-gate-logo {
                    width: 56px;
                    height: 56px;
                    background: linear-gradient(135deg, #3b82f6 0%, #4f46e5 50%, #6366f1 100%);
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 12px;
                    box-shadow: 0 8px 32px rgba(59, 130, 246, 0.35);
                }

                .license-gate-brand-text {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .license-gate-brand-name {
                    font-size: 24px;
                    font-weight: 800;
                    letter-spacing: -0.5px;
                    background: linear-gradient(135deg, #93c5fd, #a5b4fc, #c4b5fd);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .license-gate-version {
                    font-size: 9px;
                    font-weight: 700;
                    font-family: 'JetBrains Mono', monospace;
                    color: #64748b;
                    padding: 2px 6px;
                    border-radius: 5px;
                    border: 1px solid #334155;
                    background: rgba(30, 41, 59, 0.5);
                }

                .license-gate-shield {
                    width: 80px;
                    height: 80px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(239, 68, 68, 0.08));
                    border: 1px solid rgba(245, 158, 11, 0.25);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #f59e0b;
                    margin-bottom: 24px;
                    animation: lgPulse 3s ease-in-out infinite;
                }

                @keyframes lgPulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.15); }
                    50% { box-shadow: 0 0 0 12px rgba(245, 158, 11, 0); }
                }

                .license-gate-card {
                    width: 100%;
                    background: rgba(30, 41, 59, 0.5);
                    backdrop-filter: blur(24px);
                    -webkit-backdrop-filter: blur(24px);
                    border: 1px solid rgba(71, 85, 105, 0.35);
                    border-radius: 20px;
                    padding: 32px 28px;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.3);
                }

                .license-gate-title {
                    font-size: 20px;
                    font-weight: 700;
                    color: #f1f5f9;
                    text-align: center;
                    margin-bottom: 8px;
                    letter-spacing: -0.3px;
                }

                .license-gate-desc {
                    font-size: 12px;
                    color: #94a3b8;
                    text-align: center;
                    line-height: 1.6;
                    margin-bottom: 24px;
                }

                .license-gate-desc strong {
                    color: #cbd5e1;
                    font-weight: 600;
                }

                .license-gate-error {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 14px;
                    margin-bottom: 16px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                    color: #fca5a5;
                    background: rgba(239, 68, 68, 0.1);
                    border: 1px solid rgba(239, 68, 68, 0.2);
                    animation: lgShake 0.4s ease;
                }

                @keyframes lgShake {
                    0%, 100% { transform: translateX(0); }
                    20% { transform: translateX(-6px); }
                    40% { transform: translateX(6px); }
                    60% { transform: translateX(-4px); }
                    80% { transform: translateX(4px); }
                }

                .license-gate-form {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .license-gate-input-group {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .license-gate-label {
                    font-size: 11px;
                    font-weight: 600;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .license-gate-input {
                    width: 100%;
                    padding: 14px 16px;
                    background: rgba(51, 65, 85, 0.4);
                    border: 1px solid rgba(71, 85, 105, 0.5);
                    border-radius: 12px;
                    color: #f1f5f9;
                    font-size: 16px;
                    font-weight: 700;
                    font-family: 'JetBrains Mono', 'SF Mono', 'Courier New', monospace;
                    letter-spacing: 2px;
                    text-align: center;
                    outline: none;
                    transition: all 0.25s ease;
                }

                .license-gate-input::placeholder {
                    color: #475569;
                    font-weight: 500;
                    letter-spacing: 3px;
                }

                .license-gate-input:focus {
                    border-color: #f59e0b;
                    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.12), 0 0 20px rgba(245, 158, 11, 0.06);
                    background: rgba(51, 65, 85, 0.55);
                }

                .license-gate-submit {
                    width: 100%;
                    padding: 14px;
                    background: linear-gradient(135deg, #f59e0b 0%, #d97706 60%, #b45309 100%);
                    color: white;
                    font-size: 14px;
                    font-weight: 700;
                    border: none;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.25s ease;
                    box-shadow: 0 4px 20px rgba(245, 158, 11, 0.3);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    font-family: inherit;
                }

                .license-gate-submit:hover {
                    box-shadow: 0 6px 30px rgba(245, 158, 11, 0.45);
                    transform: translateY(-1px);
                    filter: brightness(1.08);
                }

                .license-gate-submit:active {
                    transform: translateY(0) scale(0.98);
                }

                .license-gate-submit:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                .license-gate-spinner {
                    width: 20px;
                    height: 20px;
                    border: 2.5px solid rgba(255,255,255,0.25);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: lgSpin 0.7s linear infinite;
                }

                @keyframes lgSpin {
                    to { transform: rotate(360deg); }
                }

                .license-gate-info {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-top: 20px;
                    padding: 12px 14px;
                    border-radius: 10px;
                    font-size: 11px;
                    color: #64748b;
                    background: rgba(51, 65, 85, 0.2);
                    border: 1px solid rgba(71, 85, 105, 0.2);
                }

                .license-gate-info svg {
                    flex-shrink: 0;
                    color: #475569;
                }

                .license-gate-logout {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    width: 100%;
                    margin-top: 12px;
                    padding: 10px;
                    background: none;
                    border: 1px solid rgba(71, 85, 105, 0.3);
                    border-radius: 10px;
                    color: #64748b;
                    font-size: 12px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-family: inherit;
                }

                .license-gate-logout:hover {
                    border-color: rgba(239, 68, 68, 0.4);
                    color: #f87171;
                    background: rgba(239, 68, 68, 0.05);
                }

                /* Success State */
                .license-gate-success {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 20px 0;
                    animation: lgFadeUp 0.4s ease-out;
                }

                .license-gate-success-icon {
                    margin-bottom: 16px;
                    animation: lgBounce 0.6s ease;
                }

                @keyframes lgBounce {
                    0% { transform: scale(0); }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); }
                }

                .license-gate-success-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #86efac;
                    margin-bottom: 8px;
                }

                .license-gate-success-desc {
                    font-size: 12px;
                    color: #94a3b8;
                }

                @media (max-width: 480px) {
                    .license-gate-card {
                        padding: 24px 20px;
                        border-radius: 16px;
                    }
                    .license-gate-brand-name {
                        font-size: 20px;
                    }
                }
            `}</style>
        </div>
    );
};

export default LicenseGate;
